import { Box, Divider, Paper, Typography } from '@mui/material'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { SelectInputForm, TextInputForm, RiskRangeNumberInputForm } from '../../../../components/common/form'
import { LoadingButton } from '@mui/lab'
import RiskMatrixParameterV2 from './RiskMatrixPrameterV2'
import toaster from '../../../../toaster'

const rangeColors = {
  low: '#19db55',
  medium: '#ffa641',
  high: '#ff8a00',
  veryHigh: '#ff2b1b',
}

const RiskMatrixForm = ({ defaultValues, onSubmit, isEdit = false, isLoading = false }) => {
  const { t } = useTranslation(['common', 'messages'])

  const schema = yup.object().shape({
    name: yup.string().required(),
    description: yup.string(),
    target: yup.string().required(),
    country: yup.string().required(),
    ranges: yup.object().shape({
      low: yup.object().shape({
        min: yup.number().min(0).required(),
        max: yup.number().required(),
        action: yup.string().optional(),
      }),
      medium: yup.object().shape({
        min: yup.number().required(),
        max: yup.number().required(),
        action: yup.string().optional(),
      }),
      high: yup.object().shape({
        min: yup.number().required(),
        max: yup.number().required(),
        action: yup.string().optional(),
      }),
      veryHigh: yup.object().shape({
        min: yup.number().required(),
        max: yup.number().required(),
        action: yup.string().optional(),
      }),
    }),
    params: yup.object().optional(),
  })

  const { handleSubmit, control, watch, formState } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  })

  const orderedKeys = ['low', 'medium', 'high', 'veryHigh']
  const [ranges, country, target] = watch(['ranges', 'country', 'target'])

  const totalRange = 100
  const calculateWidth = (rangeKey) => {
    const width = ((ranges[rangeKey].max - ranges[rangeKey].min) / totalRange) * 100
    return `${width}%`
  }

  const validateRangeOrder = (ranges) => {
    for (let i = 0; i < orderedKeys.length - 1; i++) {
      const current = ranges[orderedKeys[i]]
      const next = ranges[orderedKeys[i + 1]]

      if (current?.max >= next?.max) {
        toaster.error(
          `${t(`common:${orderedKeys[i]}`)} ${t(`common:range`)} ${t('messages:shouldBeLessThan')} ${t(
            `common:${orderedKeys[i + 1]}`,
          )}`,
        )
        return false
      }
    }
    return true
  }

  const handleSubmitWhitParmas = ({ params, ranges, ...values }) => {
    if (!validateRangeOrder(ranges)) return

    onSubmit({ ...values, params: params.rules, ranges })
  }

  return (
    <Paper elevation={0} variant="rootOutlined" sx={{ marginRight: '1rem', padding: '1.5rem 1rem 1.5rem 1.5rem' }}>
      <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <LoadingButton
          loading={isLoading}
          disabled={!formState.isValid}
          variant="contained"
          onClick={handleSubmit(handleSubmitWhitParmas)}
        >
          {isEdit ? t('common:update') : t('common:create')}
        </LoadingButton>
      </Box>

      <Box className="matrix-manual-wrapper">
        <Box className="matrix-manual-flex">
          <TextInputForm
            name="name"
            control={control}
            label={t('common:name')}
            placeholder={`${t('common:insert')} ${t('common:name')}`}
            required
          />
          <TextInputForm
            name="description"
            control={control}
            label={t('common:description')}
            placeholder={`${t('common:insert')} ${t('common:description')}`}
          />
          <SelectInputForm
            name="target"
            label={t('common:targetEntity')}
            control={control}
            defaultValue="KYB"
            options={[
              { label: t('common:business'), value: 'KYB' },
              { label: t('common:person'), value: 'files_person' },
            ]}
            emptyLabel={`${t('common:select')} ${t('common:targetEntity')}`}
            disabled={isEdit}
            required
          />
          <SelectInputForm
            name="country"
            label={t('common:country')}
            control={control}
            options={['BR', 'AR', 'CR'].map((c) => ({ label: c, value: c }))}
            emptyLabel={`${t('common:select')} ${t('common:country')}`}
            disabled={isEdit}
            required
          />
        </Box>

        <Divider />

        <Box sx={{ gridColumn: '1 / 4' }}>
          <Typography variant="title">{t('common:riskLevelScoreTitle')}</Typography>
        </Box>

        <Box sx={{ width: '60%', display: 'flex', flexFlow: 'column', gap: 2 }}>
          <Box className="matrix-manual-flex">
            {orderedKeys.map((key) => (
              <RiskRangeNumberInputForm
                key={key}
                rangesName="ranges"
                range={key}
                label={t(`common:${key}`)}
                control={control}
                errors={formState.errors}
              />
            ))}
          </Box>
          <Box className="matrix-level-bar">
            {orderedKeys.map((key) => (
              <Box
                className="matrix-level-bar-item"
                key={key}
                style={{
                  width: calculateWidth(key),
                  backgroundColor: rangeColors[key],
                  transition: 'width 0.3s ease',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                <Typography
                  variant="title2"
                  noWrap
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t(`common:${key}`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="title">{t('common:attributeAggregation')}</Typography>
            {formState.errors?.params && (
              <Typography variant="caption" color="red">
                {formState.errors.params.message}
              </Typography>
            )}
          </Box>
        </Box>
        <RiskMatrixParameterV2 control={control} target={target} country={country} />
      </Box>
    </Paper>
  )
}

export default RiskMatrixForm
